import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchVehicles,
  fetchVehicleById,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  markVehicleAsSold,
  selectVehicle,
  clearSelectedVehicle,
  setFilters,
  clearFilters,
  clearVehiclesError,
  selectAllVehicles,
  selectSelectedVehicle,
  selectVehiclesLoading,
  selectVehiclesError,
  selectVehicleFilters,
  selectFilteredVehicles,
  selectAvailableVehicles,
  selectSoldVehicles,
  selectVehiclesTotalCount,
} from '../store/slices/vehiclesSlice';
import type { CreateVehicleDto, UpdateVehicleDto, VehicleFilters } from '../types/model/vehicle.model';

/**
 * Custom hook para manejar toda la lógica de vehículos
 * Proporciona acceso al estado y acciones de Redux de forma simplificada
 */
export const useVehicles = () => {
  const dispatch = useAppDispatch();

  // Selectores
  const vehicles = useAppSelector(selectAllVehicles);
  const selectedVehicle = useAppSelector(selectSelectedVehicle);
  const isLoading = useAppSelector(selectVehiclesLoading);
  const error = useAppSelector(selectVehiclesError);
  const filters = useAppSelector(selectVehicleFilters);
  const filteredVehicles = useAppSelector(selectFilteredVehicles);
  const availableVehicles = useAppSelector(selectAvailableVehicles);
  const soldVehicles = useAppSelector(selectSoldVehicles);
  const totalCount = useAppSelector(selectVehiclesTotalCount);

  // Acciones asíncronas
  const loadVehicles = useCallback((vehicleFilters?: VehicleFilters) => {
    return dispatch(fetchVehicles(vehicleFilters));
  }, [dispatch]);

  const loadVehicleById = useCallback((id: string) => {
    return dispatch(fetchVehicleById(id));
  }, [dispatch]);

  const addVehicle = useCallback((vehicleData: CreateVehicleDto) => {
    return dispatch(createVehicle(vehicleData));
  }, [dispatch]);

  const editVehicle = useCallback((id: string, vehicleData: UpdateVehicleDto) => {
    return dispatch(updateVehicle({ id, data: vehicleData }));
  }, [dispatch]);

  const removeVehicle = useCallback((id: string) => {
    return dispatch(deleteVehicle(id));
  }, [dispatch]);

  const sellVehicle = useCallback((id: string, fechaVenta: string) => {
    return dispatch(markVehicleAsSold({ id, fechaVenta }));
  }, [dispatch]);

  // Acciones síncronas
  const selectVehicleById = useCallback((id: string) => {
    const vehicle = vehicles.find((v) => v.id === id);
    if (vehicle) {
      dispatch(selectVehicle(vehicle));
    }
  }, [dispatch, vehicles]);

  const clearSelection = useCallback(() => {
    dispatch(clearSelectedVehicle());
  }, [dispatch]);

  const updateFilters = useCallback((newFilters: VehicleFilters) => {
    dispatch(setFilters(newFilters));
  }, [dispatch]);

  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  const clearErrorMessage = useCallback(() => {
    dispatch(clearVehiclesError());
  }, [dispatch]);

  // Utilidades
  const getVehicleById = useCallback((id: string) => {
    return vehicles.find((v) => v.id === id);
  }, [vehicles]);

  const isVehicleSold = useCallback((id: string) => {
    const vehicle = getVehicleById(id);
    return vehicle ? !!vehicle.saleDate : false;
  }, [getVehicleById]);

  return {
    // Estado
    vehicles,
    selectedVehicle,
    isLoading,
    error,
    filters,
    filteredVehicles,
    availableVehicles,
    soldVehicles,
    totalCount,

    // Acciones asíncronas
    loadVehicles,
    loadVehicleById,
    addVehicle,
    editVehicle,
    removeVehicle,
    sellVehicle,

    // Acciones síncronas
    selectVehicleById,
    clearSelection,
    updateFilters,
    resetFilters,
    clearErrorMessage,

    // Utilidades
    getVehicleById,
    isVehicleSold,
  };
};
